import { supabase } from '../supabaseClient';
import {
  debriefSubmittedEmail, debriefChangesRequestedEmail, debriefApprovedEmail,
  debriefActionsAssignedEmail, debriefUnpublishedEmail, sendEmail,
} from './email';
import { notifiableOwners } from './debriefReview';

// Who hears about a debrief moving (debrief-review-approval brief §6).
//
//   submitted        → every active admin / super user, except the submitter
//   changes requested→ the author
//   approved         → the author, AND each action owner who is a real user
//   unpublished      → the author
//
// EVERY SEND IS BEST-EFFORT AND NEVER BLOCKS THE TRANSITION. The status change
// has already been written and audited by the time these run; an email that
// bounces must not make an approved debrief look unapproved. So failures are
// logged and swallowed, and each recipient is sent independently — one bad
// address doesn't cost the rest their email.
//
// The actor is never emailed about their own action. Approving a debrief and
// then being told you approved it is noise, and noise is how people learn to
// filter the whole sender.

const appUrl = () => (typeof window !== 'undefined' ? window.location.origin : '');
const debriefUrl = (d) => `${appUrl()}/debriefs/${d.id}`;

const meta = (d) => ({
  projectRef: d.project_ref || '',
  projectName: d.project_name || '',
  client: d.client || '',
  debriefUrl: debriefUrl(d),
});

// One send, wrapped. Returns true/false rather than throwing so a caller can
// count what landed without a try/catch around every recipient.
async function trySend(to, built, label) {
  if (!to) return false;
  try {
    await sendEmail({ to, subject: built.subject, html: built.html });
    return true;
  } catch (err) {
    console.warn(`debriefNotify: ${label} email failed`, { to, error: err?.message });
    return false;
  }
}

async function fetchUser(userId) {
  if (!userId) return null;
  const { data } = await supabase
    .from('internal_users')
    .select('id, full_name, email, is_active')
    .eq('id', userId)
    .maybeSingle();
  // A deactivated author gets nothing — their login is gone, so an email
  // pointing them at a page they can't open is worse than silence.
  return data && data.is_active !== false ? data : null;
}

async function fetchAdmins({ exceptId } = {}) {
  const { data } = await supabase
    .from('internal_users')
    .select('id, full_name, email, role, is_active')
    .in('role', ['admin', 'super_user']);
  return (data || []).filter(u => u.email && u.is_active !== false && u.id !== exceptId);
}

// → admins. The one notification that fans out, because "who reviews this" is
// a rota rather than a named person.
export async function notifyDebriefSubmitted(debrief, actor) {
  const admins = await fetchAdmins({ exceptId: actor?.id });
  const results = await Promise.all(admins.map(a => trySend(
    a.email,
    debriefSubmittedEmail({
      recipientName: a.full_name,
      authorName: actor?.full_name,
      ...meta(debrief),
    }),
    'submitted',
  )));
  return results.filter(Boolean).length;
}

export async function notifyDebriefChangesRequested(debrief, note, actor) {
  const author = await fetchUser(debrief.written_by);
  if (!author || author.id === actor?.id) return 0;
  const ok = await trySend(author.email, debriefChangesRequestedEmail({
    recipientName: author.full_name,
    reviewerName: actor?.full_name,
    note,
    ...meta(debrief),
  }), 'changes-requested');
  return ok ? 1 : 0;
}

export async function notifyDebriefApproved(debrief, actor) {
  let sent = 0;

  // 1. The author. Skipped when they approved it themselves — self-approval is
  //    allowed (a two-person rota would otherwise deadlock) but it does not
  //    warrant telling somebody what they just did.
  const author = await fetchUser(debrief.written_by);
  if (author && author.id !== actor?.id) {
    if (await trySend(author.email, debriefApprovedEmail({
      recipientName: author.full_name,
      approverName: actor?.full_name,
      ...meta(debrief),
    }), 'approved')) sent += 1;
  }

  // 2. Action owners — but only the ones that resolve to a Supply Pilot user.
  //    A free-text owner ("the AV team", "DZA") is a legitimate owner with
  //    nowhere to send, which is exactly why owner_user_id exists alongside
  //    owner. notifiableOwners does the filtering and the grouping, so an
  //    owner with three actions gets ONE email listing three, not three emails.
  //
  //    THE ACTION IS THE NOTE (§3) — debrief_actions has no text of its own,
  //    only a note_id. So the note body has to come along or the email lists
  //    a set of blanks.
  const { data: actions } = await supabase
    .from('debrief_actions')
    .select('id, owner, owner_user_id, timeframe, status, note:debrief_notes!note_id(body)')
    .eq('debrief_id', debrief.id)
    // Closed and dropped actions are not somebody's to-do list. Telling a new
    // owner about an action already marked done would be a straight error.
    .eq('status', 'open');

  for (const { userId, actions: theirs } of notifiableOwners(actions || [])) {
    // The actor is skipped here too — you know what you assigned yourself.
    if (userId === actor?.id) continue;
    const user = await fetchUser(userId);
    if (!user) continue;
    if (await trySend(user.email, debriefActionsAssignedEmail({
      recipientName: user.full_name,
      actions: theirs,
      ...meta(debrief),
    }), 'actions-assigned')) sent += 1;
  }

  return sent;
}

// THE ONE DISPATCHER. Both surfaces that move a debrief — the detail page and
// the review queue on Briefs — call this rather than picking a notify function
// themselves, so "who hears about a submit" has exactly one answer.
//
// `withdraw` is listed and does nothing on purpose: it's the one transition
// where the only interested party is the person who performed it. Leaving it
// out of the map would read as an oversight.
export async function notifyDebriefTransition({ debrief, action, note = null, actor }) {
  switch (action) {
    case 'submit': return notifyDebriefSubmitted(debrief, actor);
    case 'request_changes': return notifyDebriefChangesRequested(debrief, note, actor);
    case 'approve': return notifyDebriefApproved(debrief, actor);
    case 'unpublish': return notifyDebriefUnpublished(debrief, actor);
    case 'withdraw': return 0;
    default: return 0;
  }
}

export async function notifyDebriefUnpublished(debrief, actor) {
  const author = await fetchUser(debrief.written_by);
  if (!author || author.id === actor?.id) return 0;
  const ok = await trySend(author.email, debriefUnpublishedEmail({
    recipientName: author.full_name,
    actorName: actor?.full_name,
    ...meta(debrief),
  }), 'unpublished');
  return ok ? 1 : 0;
}
